import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Mic, MicOff, Settings, Loader2 } from 'lucide-react';
import useSpeechToTextAdvanced from '@/hooks/use-speech-to-text-advanced';
import AudioQualityMonitor from './AudioQualityMonitor';
import ConfidenceDisplay from './ConfidenceDisplay';
import VoiceControlSettings, { VoiceControlConfig } from './VoiceControlSettings';
import { useVoiceControl } from '@/hooks/useVoiceControl';
import type { TranscriptionConfidence, AudioQualityMetrics } from '@/hooks/speech-to-text/types';

interface AdvancedVoiceInterfaceProps {
  onTranscriptionComplete: (text: string, confidence: TranscriptionConfidence) => void;
  isProcessing?: boolean;
  disabled?: boolean;
  showQualityMonitor?: boolean;
  showConfidenceDisplay?: boolean;
}

const AdvancedVoiceInterface: React.FC<AdvancedVoiceInterfaceProps> = ({
  onTranscriptionComplete,
  isProcessing = false,
  disabled = false,
  showQualityMonitor = true,
  showConfidenceDisplay = true
}) => {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [voiceConfig, setVoiceConfig] = useState<VoiceControlConfig>({
    autoStopAfterSpeech: true,
    autoStopTimeout: 3,
    stopAfterAIResponse: false,
    pushToTalkMode: false,
    continuousMode: true
  });

  const {
    text,
    confidence,
    isListening: speechIsListening,
    startListening: speechStartListening,
    stopListening: speechStopListening,
    resetText,
    hasRecognitionSupport,
    error,
    qualityMetrics,
    environmentAnalysis,
    retranscribe,
    calibrateNoise,
    isCalibrating
  } = useSpeechToTextAdvanced({
    onResult: (text, confidence) => {
      onTranscriptionComplete(text, confidence);
      voiceControl.onAIResponseComplete();
    },
    enableNoiseReduction: true,
    enableQualityMonitoring: true,
    enableEnvironmentAdaptation: true,
    enableOpenAIFallback: true
  });

  const voiceControl = useVoiceControl({
    config: voiceConfig,
    onAutoStop: speechStopListening
  });

  // Sync voice control with speech recognition
  useEffect(() => {
    if (voiceControl.isListening && !speechIsListening) {
      speechStartListening();
    } else if (!voiceControl.isListening && speechIsListening) {
      speechStopListening();
    }
  }, [voiceControl.isListening, speechIsListening, speechStartListening, speechStopListening]);

  useEffect(() => {
    if (voiceControl.isListening && !stream) {
      navigator.mediaDevices.getUserMedia({ audio: true })
        .then(setStream)
        .catch(console.error);
    } else if (!voiceControl.isListening && stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  }, [voiceControl.isListening, stream]);

  const handleToggleListening = () => {
    if (voiceControl.isListening) {
      voiceControl.stopListening();
    } else {
      resetText();
      voiceControl.startListening();
    }
  };

  const handleQualityChange = (metrics: AudioQualityMetrics) => {
    console.log('Quality metrics updated:', metrics);
  };

  if (!hasRecognitionSupport) {
    return (
      <div className="text-center p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-sm text-red-800">
          Reconhecimento de voz não suportado neste navegador.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Main Voice Control */}
      <div className="flex items-center justify-center gap-4">
        {voiceConfig.pushToTalkMode ? (
          <Button
            onMouseDown={voiceControl.handlePushToTalkStart}
            onMouseUp={voiceControl.handlePushToTalkEnd}
            onTouchStart={voiceControl.handlePushToTalkStart}
            onTouchEnd={voiceControl.handlePushToTalkEnd}
            disabled={disabled || isProcessing || isCalibrating}
            size="lg"
            variant={voiceControl.isPushToTalkActive ? "destructive" : "default"}
            className="rounded-full w-16 h-16"
          >
            {isCalibrating ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : voiceControl.isPushToTalkActive ? (
              <MicOff className="w-6 h-6" />
            ) : (
              <Mic className="w-6 h-6" />
            )}
          </Button>
        ) : (
          <Button
            onClick={handleToggleListening}
            disabled={disabled || isProcessing || isCalibrating}
            size="lg"
            variant={voiceControl.isListening ? "destructive" : "default"}
            className="rounded-full w-16 h-16"
          >
            {isCalibrating ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : voiceControl.isListening ? (
              <MicOff className="w-6 h-6" />
            ) : (
              <Mic className="w-6 h-6" />
            )}
          </Button>
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowSettings(!showSettings)}
        >
          <Settings className="w-4 h-4" />
        </Button>

        {confidence?.needsReprocessing && (
          <Button
            variant="outline"
            size="sm"
            onClick={retranscribe}
            disabled={isProcessing}
          >
            Reprocessar
          </Button>
        )}
      </div>

      {/* Status Display */}
      <div className="text-center">
        <p className="text-sm text-gray-600">
          {isCalibrating ? 'Calibrando...' :
           voiceConfig.pushToTalkMode ? 
             (voiceControl.isPushToTalkActive ? 'Ouvindo... (solte para parar)' : 'Pressione e segure para falar') :
           voiceControl.isListening ? 'Ouvindo...' :
           isProcessing ? 'Processando...' :
           'Clique no microfone para começar'}
        </p>
        {environmentAnalysis && (
          <p className="text-xs text-gray-500 mt-1">
            Ambiente: {environmentAnalysis.environment} 
            (Confiança: {Math.round(environmentAnalysis.confidence * 100)}%)
          </p>
        )}
      </div>

      {/* Voice Control Settings */}
      <VoiceControlSettings
        config={voiceConfig}
        onConfigChange={setVoiceConfig}
        isVisible={showSettings}
      />

      {/* Advanced Settings */}
      {showSettings && (
        <div className="space-y-3">
          <Button
            variant="outline"
            size="sm"
            onClick={calibrateNoise}
            disabled={isCalibrating || !stream}
            className="w-full"
          >
            {isCalibrating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Calibrando Ruído...
              </>
            ) : (
              'Calibrar Ruído de Fundo'
            )}
          </Button>

          {environmentAnalysis?.recommendations && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <h5 className="text-xs font-medium text-blue-800 mb-2">
                Recomendações:
              </h5>
              <ul className="text-xs text-blue-700 space-y-1">
                {environmentAnalysis.recommendations.map((rec, index) => (
                  <li key={index}>• {rec}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Quality Monitor */}
      {showQualityMonitor && stream && (
        <AudioQualityMonitor
          stream={stream}
          onQualityChange={handleQualityChange}
        />
      )}

      {/* Confidence Display */}
      {showConfidenceDisplay && confidence && (
        <ConfidenceDisplay
          confidence={confidence}
          onRetranscribe={retranscribe}
          showWordLevel={showSettings}
        />
      )}

      {/* Error Display */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{error.message}</p>
          {error.code && (
            <p className="text-xs text-red-600 mt-1">Código: {error.code}</p>
          )}
        </div>
      )}

      {/* Current Transcript */}
      {text && (
        <div className="p-3 bg-gray-50 border rounded-lg">
          <p className="text-sm text-gray-800">{text}</p>
        </div>
      )}
    </div>
  );
};

export default AdvancedVoiceInterface;
